// «Как это работает» (макет, версия 34): шесть шагов от селфи до посылки, гарантия возврата,
// лозунг и выбор направления. Сроков доставки ещё нет — их на странице нет; миры — из каталога.
import type { Catalog } from "../../api/types";
import { useCatalog } from "../../app/queries";
import { lowestPrintPrice, formatRub } from "../../site/prices";
import { SiteFooter } from "../../site/SiteFooter";
import { SiteHeader } from "../../site/SiteHeader";
import { Button } from "../../ui/Button";
import { Icon } from "../../ui/icons";
import { Tag } from "../../ui/Message";
import { MAIN_SLOGAN, Slogan } from "../../ui/Slogan";
import styles from "./How.module.css";
import { SIZES_HALF, SIZES_THIRD, picture, type PicturePriority } from "../../site/pictures";

function lowestPrice(catalog: Catalog | undefined): number | null {
  if (catalog === undefined) return null;
  const prices = [lowestPrintPrice(catalog, "miniature"), lowestPrintPrice(catalog, "gift")].filter(
    (price): price is number => price !== null,
  );
  return prices.length === 0 ? null : Math.min(...prices);
}

// Шаг «Придумай образ» (Иван, 01.11.2026): без «шести миров» — своими словами или герой из каталога.
const LOOKS_TEXT = "Кем угодно: пиратом, рыцарем, космонавтом. Для настолки — готовые герои в каталоге.";

// Три картинки к шагу «Придумай образ»: миры каталога, а если мир один — ещё раса, и подарок.
function lookPictures(catalog: Catalog | undefined): { image: string; label: string }[] {
  const worlds = catalog?.miniature.worlds ?? [];
  const pictures = worlds.slice(0, 2).map((world) => ({ image: `/catalog/setting_${world.id}.jpg`, label: world.title_ru }));
  if (pictures.length < 2) {
    pictures.push({ image: "/catalog/race_mountain_dwarf.jpg", label: "горный дварф" });
  }
  pictures.push({ image: "/showcase/gift_shelf.jpg", label: "пират" });
  return pictures;
}

const FREE = <Tag tone="ok">бесплатно</Tag>;

export function HowItWorksScreen() {
  const { data: catalog } = useCatalog();
  const fromPrice = lowestPrice(catalog);
  return (
    <div className={styles.page}>
      <SiteHeader />
      <main className={styles.main}>
        <section className={styles.intro}>
          <p className="eyebrow">как это работает</p>
          <h1 className="h1">От селфи до фигурки на полке</h1>
          <p className="text muted">Эскиз — бесплатно. Платишь, когда решишь заказать фигурку.</p>
          <div className={styles.facts}>
            <div className={styles.fact}>
              <span className={styles.factValue}>0 ₽</span>
              <span className="small muted">эскиз</span>
            </div>
            <div className={styles.fact}>
              <span className={styles.factValue}>≈ 1 мин</span>
              <span className="small muted">ждать эскиз</span>
            </div>
          </div>
        </section>

        <ol className={styles.steps}>
          <Step number={1} title="Сделай селфи" tag={FREE}>
            <p className="small muted">
              Для большего сходства добавь фото вполоборота и в профиль.
            </p>
            <div className={`${styles.pictures} ${styles.two}`}>
              <Picture image="/tips/selfie_front_clear.jpg" alt="Селфи анфас" label="анфас" aspect="3 / 4" priority="lead" />
              <Picture
                image="/tips/selfie_34_clear.jpg"
                alt="Селфи в три четверти"
                label="3/4 · по желанию"
                aspect="3 / 4"
                priority="eager"
              />
            </div>
          </Step>
          <Step number={2} title="Придумай образ" tag={FREE}>
            <p className="small muted">{LOOKS_TEXT}</p>
            <div className={`${styles.pictures} ${styles.three}`}>
              {lookPictures(catalog).map((lookPicture) => (
                <Picture
                  key={lookPicture.image}
                  image={lookPicture.image}
                  alt=""
                  label={lookPicture.label}
                  aspect="1"
                  sizes={SIZES_THIRD}
                />
              ))}
            </div>
          </Step>
          <Step number={3} title="Посмотри эскиз" tag={FREE}>
            <p className="small muted">
              Примерно через минуту: спереди, сбоку, сзади и лицо крупно. Не то — поменяем бесплатно, хоть весь
              образ.
            </p>
            <Picture
              image="/showcase/example_concept.jpg"
              alt="Эскиз фигурки лучницы спереди, сбоку и сзади"
              label="эскиз · пример, лицо сгенерировано"
              aspect="900 / 596"
            />
          </Step>
          <Step
            number={4}
            title="Закажи свою фигурку"
            tag={fromPrice !== null ? <Tag tone="neutral">от {formatRub(fromPrice)}</Tag> : null}
          >
            <p className="small muted">
              Покажем её серой — такой она выйдет из принтера. Выбери размер — и мы начинаем её делать.
            </p>
            <div className={`${styles.pictures} ${styles.two}`}>
              <Picture image="/showcase/example_concept_front.jpg" alt="Эскиз фигурки" label="эскиз" aspect="3 / 4" contain />
              <Picture image="/showcase/example_grey_front.jpg" alt="Серая версия фигурки" label="серая печать" aspect="3 / 4" contain background="#fefefe" />
            </div>
          </Step>
          <Step number={5} title="Проверь 3D-модель" tag={<Tag tone="ok">гарантия возврата</Tag>}>
            <p className="small muted">
              После оплаты строим 3D-модель — её можно покрутить и приблизить. Не понравится — переделаем или вернём
              деньги.
            </p>
            <div className={styles.picture} style={{ height: 200, background: "#fefefe" }}>
              <img
                {...picture("/showcase/example_grey_front.jpg", SIZES_HALF)}
                alt="3D-модель фигурки в окне просмотра"
                style={{ objectFit: "contain" }}
              />
              <span className={styles.spin}>
                <Icon name="retry" size={14} />
                крути пальцем
              </span>
            </div>
          </Step>
          <Step number={6} title="Печатаем и отправляем" last>
            <p className="small muted">
              Профессиональный FDM-принтер: сопло 0,2 мм, высота слоя 0,08 мм. Снимаем поддержки вручную, проверяем и
              отправляем. Где заказ — видно в «Моих заказах».
            </p>
            <Picture
              image="/showcase/scale_tabletop.jpg"
              alt="Серая миниатюра на игровом поле рядом с фигурками из магазина"
              label="иллюстрация · миниатюра рядом с покупными"
              aspect="1400 / 933"
            />
          </Step>
        </ol>

        <section className={styles.guarantee} aria-label="Гарантия возврата">
          <Icon name="shield" size={26} />
          <span style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span className="h3">Гарантия возврата</span>
            <span className="small">
              Платишь только после бесплатного эскиза. После оплаты покажем 3D-модель: не понравится — переделаем или
              вернём деньги.
            </span>
          </span>
        </section>

        <Slogan title={MAIN_SLOGAN}>Придумай героя — остальное за нами.</Slogan>

        <section className={styles.directions} aria-label="Что хочешь сделать">
          <div className="mini">
            <Button to="/mini">Миниатюра для игры</Button>
          </div>
          <div className="gift">
            <Button to="/gift">Фигурка в любом образе</Button>
          </div>
          <p className="small muted" style={{ textAlign: "center" }}>
            Эскиз — бесплатно
          </p>
        </section>
      </main>
      <SiteFooter
        links={[
          { to: "/faq", label: "Вопросы" },
          { to: "/docs", label: "Документы" },
          { to: "/docs/refund", label: "Возврат" },
        ]}
      />
    </div>
  );
}

function Step({
  number,
  title,
  tag = null,
  last = false,
  children,
}: {
  number: number;
  title: string;
  tag?: React.ReactNode;
  last?: boolean;
  children: React.ReactNode;
}) {
  return (
    <li className={styles.step}>
      <span className={styles.number} aria-hidden="true">
        {number}
      </span>
      {!last && <span className={styles.line} aria-hidden="true" />}
      <div className={styles.stepHead}>
        <h2 className="h3">
          <span className="visually-hidden">Шаг {number}. </span>
          {title}
        </h2>
        {tag}
      </div>
      {children}
    </li>
  );
}

function Picture({
  image,
  alt,
  label,
  aspect,
  contain = false,
  background,
  priority = "lazy",
  sizes = SIZES_HALF,
}: {
  image: string;
  alt: string;
  label: string;
  aspect: string;
  contain?: boolean;
  priority?: PicturePriority;
  sizes?: string;
  // Фон под картинкой целиком — того же цвета, что её собственный фон, чтобы не было стыка.
  background?: string;
}) {
  return (
    <div className={styles.picture} style={{ aspectRatio: aspect, background }}>
      <img {...picture(image, sizes, priority)} alt={alt} style={contain ? { objectFit: "contain" } : undefined} />
      <span className={styles.label}>{label}</span>
    </div>
  );
}
